import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FileText, User, DollarSign, Calendar, Image as ImageIcon, File as FileIcon,
  ArrowLeft, Check, MessageSquare, Loader2, ExternalLink, X, Download, XCircle,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * /dashboard/vendor/inquiries/:inquiryId — Fiverr-style detail view.
 *
 * Shows: requester profile, full form data (title, desc, budget, deadline),
 * photo + PDF references, and two CTAs:
 *   - Accept Buyer's Budget (creates a pending-payment order at their price)
 *   - Submit Counter-Offer (vendor sets their own price + delivery days)
 */
const resolveUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  if (u.startsWith('/api/')) return `${BACKEND}${u}`;
  return u;
};

const isImage = (url) => /\.(jpe?g|png|gif|webp|svg|heic|heif)(\?|$)/i.test(url);
const isPdf = (url) => /\.pdf(\?|$)/i.test(url);

function AttachmentTile({ url, idx, onOpen }) {
  const resolved = resolveUrl(url);
  const filename = url.split('/').pop()?.split('?')[0] || 'file';
  if (isImage(url)) {
    return (
      <button
        type="button"
        onClick={() => onOpen({ url: resolved, type: 'image', filename })}
        className="group relative aspect-square rounded-xl overflow-hidden border border-white/10 hover:border-fuchsia-400/60 transition-all text-left"
        data-testid={`inquiry-attachment-${idx}`}
      >
        <img src={resolved} alt={filename} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/45 transition-all flex items-end p-2">
          <span className="opacity-0 group-hover:opacity-100 text-white text-[10px] font-bold tracking-wider">
            CLICK TO ENLARGE
          </span>
        </div>
      </button>
    );
  }
  if (isPdf(url)) {
    return (
      <button
        type="button"
        onClick={() => onOpen({ url: resolved, type: 'pdf', filename })}
        className="group aspect-square rounded-xl overflow-hidden border border-fuchsia-400/30 bg-fuchsia-500/[0.08] hover:border-fuchsia-400/60 transition-all text-left relative"
        data-testid={`inquiry-attachment-${idx}`}
      >
        {/* True PDF preview via <embed>. Falls back to icon overlay on browsers
            that refuse to render the embed in a tile-sized container. */}
        <embed
          src={`${resolved}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
          type="application/pdf"
          className="w-full h-full pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#13153A]/95 via-transparent to-transparent flex flex-col justify-end p-2">
          <p className="text-fuchsia-200 text-[10px] font-bold uppercase tracking-wider truncate">PDF · {filename.slice(0, 14)}</p>
          <p className="text-white/70 text-[9px]">Click to preview</p>
        </div>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen({ url: resolved, type: 'file', filename })}
      className="aspect-square rounded-xl border border-white/10 hover:border-white/30 flex flex-col items-center justify-center p-3 text-center bg-white/5 transition-all"
      data-testid={`inquiry-attachment-${idx}`}
    >
      <FileIcon size={22} className="text-zinc-300 mb-2" />
      <p className="text-white text-[10px] font-bold truncate w-full">{filename.slice(0, 14)}</p>
      <p className="text-zinc-400 text-[9px] mt-1">Click to open</p>
    </button>
  );
}

function AttachmentLightbox({ open, onClose, item }) {
  if (!open || !item) return null;
  return (
    <div
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-md flex flex-col"
      onClick={onClose}
      data-testid="attachment-lightbox"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10 bg-[#0E0E23]/80">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.18em] text-fuchsia-300/80 font-bold">Reference attachment</p>
          <p className="text-white text-sm font-semibold truncate">{item.filename}</p>
        </div>
        <div className="flex items-center gap-2 ml-3">
          <a
            href={item.url}
            target="_blank"
            rel="noreferrer"
            download
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white text-xs font-bold"
            data-testid="lightbox-download"
          >
            <Download size={13} /> Download
          </a>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-white"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden" onClick={onClose}>
        {item.type === 'image' && (
          <img
            src={item.url}
            alt={item.filename}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)]"
          />
        )}
        {item.type === 'pdf' && (
          <embed
            src={`${item.url}#toolbar=1`}
            type="application/pdf"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl h-[85vh] rounded-xl bg-white shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)]"
          />
        )}
        {item.type === 'file' && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="rounded-2xl bg-[#13153A] border border-white/10 px-8 py-10 text-center max-w-sm"
          >
            <FileIcon size={48} className="text-fuchsia-300 mx-auto mb-3" />
            <p className="text-white font-bold">{item.filename}</p>
            <p className="text-zinc-400 text-xs mt-1">This file type can't be previewed inline.</p>
            <a
              href={item.url}
              target="_blank"
              rel="noreferrer"
              download
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white font-bold text-sm"
            >
              <Download size={14} /> Download
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InquiryDetail() {
  const { inquiryId } = useParams();
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null); // 'accept' | 'counter' | 'decline' | null
  const [price, setPrice] = useState('');
  const [days, setDays] = useState('7');
  const [scopeNote, setScopeNote] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lightbox, setLightbox] = useState(null); // {url, type, filename} | null
  const [stripeStatus, setStripeStatus] = useState(null); // {connected, can_accept_jobs, id_verification_status, ...}

  useEffect(() => {
    const loadStripe = async () => {
      try {
        const res = await fetch(`${API}/stripe/connect/status`, { headers: getAuthHeaders() });
        if (res.ok) setStripeStatus(await res.json());
      } catch { /* leave null — UI will treat as unknown / disabled */ }
    };
    loadStripe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        // The existing /messages/inquiries returns the list. Pick by id.
        const res = await fetch(`${API}/messages/inquiries`, { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          const found = (data.inquiries || []).find((i) => i.message_id === inquiryId);
          setInquiry(found || null);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inquiryId]);

  // Try to parse a numeric budget from the buyer's free-text input
  const numericBudget = (() => {
    if (!inquiry?.budget) return null;
    const cleaned = String(inquiry.budget).replace(/[$,]/g, '').trim();
    const first = cleaned.split(/\s+/)[0];
    const n = parseFloat(first);
    return Number.isFinite(n) ? n : null;
  })();

  const submit = async () => {
    if (mode === 'counter' && (!price || Number(price) <= 0)) {
      toast.error('Enter a counter-offer price');
      return;
    }
    if (mode === 'accept' && numericBudget == null) {
      toast.error("Buyer's budget isn't numeric — submit a counter-offer instead");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        inquiry_id: inquiryId,
        price: mode === 'counter' ? Number(price) : undefined,
        delivery_days: Number(days) || 7,
        scope_note: scopeNote.trim() || undefined,
      };
      const res = await fetch(`${API}/custom-orders/accept-inquiry`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(
          mode === 'accept'
            ? 'Inquiry accepted! Order created — waiting for buyer payment.'
            : 'Counter-offer sent! Waiting for buyer to pay.',
        );
        navigate(`/dashboard/vendor/orders/${data.order.order_id}`);
      } else if (res.status === 402 && typeof data.detail === 'object' && data.detail?.code === 'stripe_setup_required') {
        toast.error('Set up Stripe + verify your ID first.', {
          description: data.detail.message,
          duration: 8000,
          action: { label: 'Set up now', onClick: () => navigate('/dashboard/vendor/payments') },
        });
      } else {
        toast.error((typeof data.detail === 'string' ? data.detail : data.detail?.message) || 'Could not accept inquiry');
      }
    } finally { setSubmitting(false); }
  };

  const decline = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/messages/inquiries/${inquiryId}/decline`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason.trim() || undefined }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Inquiry declined. Buyer has been notified.');
        navigate('/dashboard/vendor/inquiries');
      } else {
        toast.error(data.detail || 'Could not decline this inquiry');
      }
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-fuchsia-300" />
      </div>
    );
  }
  if (!inquiry) {
    return (
      <div className="space-y-4">
        <button onClick={() => navigate(-1)} className="text-fuchsia-300 text-sm inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back
        </button>
        <GlassCard className="p-8 text-center">
          <p className="text-white font-semibold">Inquiry not found</p>
          <p className="text-zinc-400 text-xs mt-1">It may have been accepted already.</p>
          <Link to="/dashboard/vendor/inquiries" className="mt-3 inline-block text-fuchsia-300 text-sm hover:underline">
            ← Back to all inquiries
          </Link>
        </GlassCard>
      </div>
    );
  }

  const already = inquiry.inquiry_meta?.status === 'accepted';
  const declined = inquiry.inquiry_meta?.status === 'declined';
  const linkedOrderId = inquiry.inquiry_meta?.custom_order_id;
  const attachments = inquiry.attachment_urls || [];

  return (
    <div className="space-y-5 max-w-4xl mx-auto" data-testid="inquiry-detail">
      <div className="flex items-center justify-between gap-3">
        <Link to="/dashboard/vendor/inquiries" className="text-fuchsia-300 text-sm inline-flex items-center gap-1 hover:underline">
          <ArrowLeft size={14} /> All inquiries
        </Link>
        {already && linkedOrderId && (
          <Link
            to={`/dashboard/vendor/orders/${linkedOrderId}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-500/20 text-emerald-200 border border-emerald-500/30"
            data-testid="linked-order-cta"
          >
            <Check size={12} /> Already accepted · View order
          </Link>
        )}
        {declined && (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-red-500/15 text-red-200 border border-red-500/30"
            data-testid="declined-badge"
          >
            <XCircle size={12} /> Declined
          </span>
        )}
      </div>

      {/* Header — requester + title */}
      <GlassCard className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-[#8B5CF6]/20 flex items-center justify-center overflow-hidden flex-shrink-0">
            {inquiry.sender_picture
              ? <img src={inquiry.sender_picture} alt="" className="w-full h-full object-cover" />
              : <User size={22} className="text-[#A78BFA]" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">REQUEST FROM</p>
            <h1 className="text-xl sm:text-2xl text-white font-syne font-bold leading-tight">{inquiry.sender_name}</h1>
            <p className="text-zinc-400 text-xs mt-1">
              {inquiry.created_at && new Date(inquiry.created_at).toLocaleString()}
              {inquiry.is_custom_project && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  ✨ Custom project
                </span>
              )}
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Form snapshot — what the customer requested */}
      <GlassCard className="p-6 space-y-5" data-testid="inquiry-form-snapshot">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-fuchsia-300 font-bold mb-1.5">REQUEST DETAILS</p>
          <h2 className="text-white font-syne text-lg font-bold">
            {(inquiry.inquiry_meta?.title) || inquiry.service_description?.split('\n')[0]?.slice(0, 80)}
          </h2>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 flex items-center gap-1">
            <FileText size={11} /> Services / Description
          </p>
          <p className="text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap bg-white/[0.03] border border-white/10 rounded-xl p-4">
            {inquiry.service_description || '—'}
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-1 mb-1.5">
              <DollarSign size={11} /> Buyer's budget
            </p>
            <p className="text-white text-base font-bold">
              {inquiry.budget || <span className="text-zinc-500 font-normal text-sm">Not provided</span>}
            </p>
            {numericBudget != null && (
              <p className="text-[10px] text-emerald-300 mt-0.5">≈ ${numericBudget.toFixed(2)} numeric</p>
            )}
          </div>
          <div className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-1 mb-1.5">
              <Calendar size={11} /> Need by
            </p>
            <p className="text-white text-base font-bold">
              {inquiry.inquiry_meta?.deadline || <span className="text-zinc-500 font-normal text-sm">Flexible</span>}
            </p>
          </div>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold flex items-center gap-1 mb-2">
              <ImageIcon size={11} /> Reference attachments ({attachments.length})
            </p>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2.5">
              {attachments.map((u, i) => <AttachmentTile key={i} url={u} idx={i} onOpen={setLightbox} />)}
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">Click any tile to preview full-size in a popup.</p>
          </div>
        )}
      </GlassCard>

      {declined && (
        <GlassCard className="p-5 border-red-400/30 bg-red-500/[0.05]" data-testid="declined-banner">
          <p className="text-red-100 font-bold flex items-center gap-2">
            <XCircle size={16} /> You declined this inquiry
          </p>
          {inquiry.inquiry_meta?.decline_reason && (
            <p className="text-red-100/80 text-sm mt-2 italic">
              &ldquo;{inquiry.inquiry_meta.decline_reason}&rdquo;
            </p>
          )}
          {inquiry.inquiry_meta?.declined_at && (
            <p className="text-red-100/60 text-[11px] mt-1">
              {new Date(inquiry.inquiry_meta.declined_at).toLocaleString()}
            </p>
          )}
        </GlassCard>
      )}

      {/* Action buttons */}
      {!already && !declined && (
        <GlassCard className="p-6" data-testid="inquiry-actions">
          {/* Stripe setup gate — show a clear banner when the vendor can't yet accept paid jobs. */}
          {stripeStatus && !stripeStatus.can_accept_jobs && !mode && (
            <div
              className="mb-4 rounded-2xl border border-yellow-400/40 bg-gradient-to-r from-yellow-500/10 via-amber-500/[0.08] to-yellow-500/10 p-4"
              data-testid="stripe-setup-banner"
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-yellow-400/20 border border-yellow-400/40 flex items-center justify-center flex-shrink-0">
                  <DollarSign size={18} className="text-yellow-300" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-yellow-100 font-bold text-sm">
                    {stripeStatus.has_account
                      ? 'Finish setting up Stripe to accept this job'
                      : 'Set up Stripe to accept paid jobs'}
                  </p>
                  <p className="text-yellow-100/80 text-xs mt-1">
                    {stripeStatus.id_verification_status === 'pending'
                      ? "Your ID is under review by Stripe — you'll be able to accept once they confirm."
                      : (stripeStatus.currently_due && stripeStatus.currently_due.length > 0)
                        ? `Stripe still needs ${stripeStatus.currently_due.length} item${stripeStatus.currently_due.length === 1 ? '' : 's'} (incl. ID document) before you can receive payouts.`
                        : 'Buyer payments + escrow can\'t be released to you until Stripe verifies your identity. Takes ~3 minutes.'}
                  </p>
                  <Link
                    to="/dashboard/vendor/payments"
                    className="mt-3 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-[#0E0E23] text-xs font-bold transition-all"
                    data-testid="stripe-setup-cta"
                  >
                    {stripeStatus.has_account ? 'Continue Stripe setup' : 'Set up Stripe now'} →
                  </Link>
                  <p className="text-yellow-100/60 text-[10px] mt-2 italic">
                    Tip: You can still <span className="font-bold">Deny</span> this inquiry without Stripe — the buyer will be notified.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!mode && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => { setMode('accept'); setPrice(numericBudget?.toString() || ''); }}
                disabled={numericBudget == null || (stripeStatus && !stripeStatus.can_accept_jobs)}
                className="rounded-2xl p-4 bg-gradient-to-br from-emerald-500 to-teal-500 hover:shadow-[0_12px_30px_-10px_rgba(16,185,129,0.6)] text-white font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="accept-budget-btn"
                title={
                  stripeStatus && !stripeStatus.can_accept_jobs
                    ? 'Finish Stripe setup + ID verification to accept paid jobs'
                    : numericBudget == null
                      ? "Buyer's budget isn't numeric"
                      : ''
                }
              >
                <Check size={18} className="inline mr-2" />
                Accept buyer's budget
                {numericBudget != null && <span className="block text-emerald-100 text-xs font-normal mt-1">≈ ${numericBudget.toFixed(2)}</span>}
              </button>
              <button
                onClick={() => { setMode('counter'); setPrice(numericBudget?.toString() || ''); }}
                disabled={!!(stripeStatus && !stripeStatus.can_accept_jobs)}
                className="rounded-2xl p-4 bg-gradient-to-br from-fuchsia-500 to-violet-600 hover:shadow-[0_12px_30px_-10px_rgba(168,85,247,0.6)] text-white font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="counter-offer-btn"
                title={stripeStatus && !stripeStatus.can_accept_jobs ? 'Finish Stripe setup + ID verification to send a counter-offer' : ''}
              >
                <DollarSign size={18} className="inline mr-2" />
                Submit counter-offer
                <span className="block text-fuchsia-100 text-xs font-normal mt-1">Adjust price + delivery</span>
              </button>
              <button
                onClick={() => setMode('decline')}
                className="rounded-2xl p-4 border-2 border-red-400/40 bg-red-500/[0.08] hover:bg-red-500/15 hover:border-red-400/70 text-red-100 font-bold text-sm transition-all"
                data-testid="decline-job-btn"
              >
                <XCircle size={18} className="inline mr-2" />
                Deny job
                <span className="block text-red-200/80 text-xs font-normal mt-1">Not the right fit</span>
              </button>
            </div>
          )}

          {mode === 'decline' && (
            <div className="space-y-4" data-testid="decline-composer">
              <p className="text-white font-bold flex items-center gap-2">
                <XCircle size={18} className="text-red-300" />
                Deny this job
              </p>
              <p className="text-zinc-400 text-sm">
                The buyer will be notified that you can't take this on. Your reason is optional — but a short note keeps the relationship warm and helps the buyer find a better-matched vendor.
              </p>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Reason (optional)</label>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value.slice(0, 1000))}
                  rows={3}
                  placeholder="e.g. 'I'm booked solid through next month — try {other vendor} who does great work in this style.'"
                  className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3 focus:border-red-400/60 focus:outline-none resize-none"
                  data-testid="decline-reason"
                />
                <p className="text-[10px] text-zinc-500 mt-1 text-right">{declineReason.length}/1000</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={decline}
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold text-sm inline-flex items-center gap-2 hover:shadow-[0_0_24px_rgba(244,63,94,0.4)] transition-all disabled:opacity-50"
                  data-testid="confirm-decline-btn"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  <XCircle size={14} /> Confirm — Decline job
                </button>
                <button onClick={() => setMode(null)} className="px-4 py-2.5 rounded-xl text-zinc-300 hover:bg-white/5 text-sm">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {(mode === 'accept' || mode === 'counter') && (
            <div className="space-y-4">
              <p className="text-white font-bold">
                {mode === 'accept' ? 'Confirm accepting buyer\'s budget' : 'Submit counter-offer'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {mode === 'counter' && (
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Your Price (USD) *</label>
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="250"
                      className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-base p-3 focus:border-fuchsia-400/60"
                      data-testid="counter-price"
                    />
                  </div>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">Delivery (days) *</label>
                  <input
                    type="number"
                    value={days}
                    onChange={(e) => setDays(e.target.value)}
                    min={1}
                    max={180}
                    className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-base p-3 focus:border-fuchsia-400/60"
                    data-testid="counter-days"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold mb-1.5 block">
                  Optional note to buyer
                </label>
                <textarea
                  value={scopeNote}
                  onChange={(e) => setScopeNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. 'I can include 3 logo variants, source files, and 2 rounds of revisions for this price.'"
                  className="w-full bg-white/5 border border-white/10 rounded-xl text-white text-sm p-3 focus:border-fuchsia-400/60"
                  data-testid="counter-note"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="submit-offer-btn"
                >
                  {submitting && <Loader2 size={14} className="animate-spin" />}
                  {mode === 'accept' ? 'Confirm & Send to Buyer' : 'Send Counter-Offer'}
                </button>
                <button onClick={() => setMode(null)} className="px-4 py-2.5 rounded-xl text-zinc-300 hover:bg-white/5 text-sm">
                  Cancel
                </button>
              </div>
              <p className="text-[11px] text-zinc-500">
                The buyer will receive a notification + email with a "Pay ${price || numericBudget || '—'}" button.
                Once paid, the order will appear in your Orders tab and the {3}-day delivery clock starts.
              </p>
            </div>
          )}
        </GlassCard>
      )}

      {/* Chat shortcut */}
      <GlassCard className="p-4">
        <Link
          to={`/messages/${inquiry.sender_id}`}
          className="inline-flex items-center gap-2 text-fuchsia-300 hover:text-fuchsia-200 text-sm"
        >
          <MessageSquare size={14} /> Message {inquiry.sender_name} directly
        </Link>
      </GlassCard>

      <AttachmentLightbox open={!!lightbox} onClose={() => setLightbox(null)} item={lightbox} />
    </div>
  );
}
